import { useRef, useState, useEffect } from "react";
import { ToolContainer, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Camera, ScrollText, Copy, Crop, Pencil, Highlighter, RotateCw, ZoomIn, ZoomOut, Undo2, MousePointer2, Maximize, Minimize, MoveUpRight, Square, Type, Grid2x2 } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useShareGate } from "@/hooks/useShareGate";
import { ScrollStitcher, MAX_STITCH_HEIGHT, type AddStatus } from "./stitchScroll";

const TOOL_INFO_CONTENT = {
  what: `Captures a screenshot of your screen, a specific window, or a browser tab, then lets you crop it down to just the region you want (or, for a long page, record it as you scroll and stitch the pieces into one tall image), rotate it, mark it up with a pencil, highlighter, arrows, boxes and text, hide private details by pixelating them, and copy or download the result.`,
  how: `Optionally set a delay first, click Take Screenshot and choose what to capture in the browser's own sharing prompt, then drag to crop the captured image down to just the part you need — tap Fullscreen first if the screen feels too small to work with precisely, especially on mobile. From there, rotate if needed, and use the pencil or highlighter to mark it up before copying or downloading.\n\nFor a page taller than the screen, choose Capture whole page, share the tab or window, then scroll it slowly yourself and press Finish at the end. Each picture is lined up with the one before and joined, in your browser; whatever you didn't scroll past is not in the result.`,
  tip: `A website can't draw a selection box directly onto your screen before capturing, the way a native OS screenshot tool can — that's blocked by browser security, since otherwise any site could screen-record parts of your screen at will. The browser's own "choose what to share" prompt is unavoidable for the same reason — no website can remove or customize it. This tool works around that the practical way: capture the full screen/window/tab first, then crop and mark it up afterward, which gets you the same result. Copy to Clipboard needs a browser that supports writing images to the clipboard (Chrome, Edge, and Safari all do); Download always works as a fallback.\n\nIf you choose "Entire Screen" in that prompt, your browser itself typically drops to the background right after — this is normal OS/browser behavior, not a bug: since "entire screen" is meant to capture your whole desktop, the browser has to step out of the way so whatever's underneath is actually visible to capture, rather than just capturing a picture of itself. If you want to bring another window to the front first (to make sure it's the thing actually captured), set a short delay below before clicking Take Screenshot — that's specifically what the delay option is for.`,
};

type Stage = "idle" | "counting-down" | "scrolling" | "cropping" | "editing";
type DrawTool = "select" | "pencil" | "highlighter" | "arrow" | "rect" | "text" | "blur";
const SHAPE_TOOLS: DrawTool[] = ["arrow", "rect", "blur"];
type Rotation = 0 | 90 | 180 | 270;

/** What the person sees after each frame of a whole-page (scrolling) capture. */
const SCROLL_HINTS: Record<AddStatus, string> = {
  first: "Scroll the page you shared, slowly. Come back here and press Finish when you reach the end.",
  added: "",
  same: "",
  up: "That frame was above what is already captured, so nothing was added. Scroll down past the last captured part.",
  lost: "That jump could not be lined up: the page moved too far between frames, or its content changed. Scroll back up a little and go slower.",
  ambiguous: "That part of the page repeats or is blank, so it can not be lined up safely. Scroll back up a little and move on in smaller steps.",
  resized: "The shared window changed size, so that frame was skipped. Put it back to the original size.",
  limit: `Reached the maximum height (${MAX_STITCH_HEIGHT.toLocaleString("en-US")} px). Capture finished.`,
};

const DELAY_OPTIONS = [0, 2, 5, 10] as const;
const PENCIL_COLORS = ["#ef4444", "#3b82f6", "#22c55e", "#000000", "#ffffff"];
const HIGHLIGHTER_COLORS = ["#fde047", "#86efac", "#93c5fd", "#f9a8d4"];

export default function ScreenshotTool() {
  const { gateShare } = useShareGate();
  const [shotFormat, setShotFormat] = useState<"png" | "jpg">("png");
  const [stage, setStage] = useState<Stage>("idle");
  const [delaySeconds, setDelaySeconds] = useState<(typeof DELAY_OPTIONS)[number]>(0);
  const [countdown, setCountdown] = useState(0);
  const [copySupported, setCopySupported] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenTargetRef = useRef<HTMLDivElement | null>(null);

  // Raw capture, before any cropping -- kept separate from the working
  // canvas so "crop" always starts from the real, full captured frame.
  const [rawCaptureUrl, setRawCaptureUrl] = useState<string | null>(null);
  const [selection, setSelection] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);

  const [rotation, setRotation] = useState<Rotation>(0);
  const [zoom, setZoom] = useState(1);
  const [activeTool, setActiveTool] = useState<DrawTool>("select");
  const [penColor, setPenColor] = useState(PENCIL_COLORS[0]);
  const [penThickness, setPenThickness] = useState(3);
  const [highlighterColor, setHighlighterColor] = useState(HIGHLIGHTER_COLORS[0]);
  const [highlighterThickness, setHighlighterThickness] = useState(18);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  // Whole-page (scrolling) capture: frames are stitched in the browser as the shared page is scrolled.
  const stitcherRef = useRef<ScrollStitcher | null>(null);
  const scrollTimerRef = useRef<number | null>(null);
  const [scrollInfo, setScrollInfo] = useState<{ frames: number; height: number; hint: string }>({ frames: 0, height: 0, hint: "" });
  const [stitchNote, setStitchNote] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  // The <video> element that stays continuously attached to and playing
  // the stream throughout the whole countdown -- see startCapture for
  // why this needs to exist at all (a stream with nothing consuming it
  // can be throttled by the browser, silently defeating the delay).
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rawImgRef = useRef<HTMLImageElement | null>(null);
  const cropImgRef = useRef<HTMLImageElement | null>(null);
  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  const editCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pendingEditorLoadRef = useRef<{ img: HTMLImageElement; rot: Rotation } | null>(null);
  const isDrawingRef = useRef(false);
  const shapeRef = useRef<{ start: { x: number; y: number }; base: ImageData } | null>(null);
  // Text being typed on the picture: where it will go (canvas pixels) and where the box is on screen.
  const [textDraft, setTextDraft] = useState<{ cx: number; cy: number; left: number; top: number; value: string } | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setCopySupported(typeof ClipboardItem !== "undefined" && !!navigator.clipboard?.write);
  }, []);

  // Tracks fullscreen state from any source -- the browser's own Escape
  // key or UI can exit fullscreen without ever calling toggleFullscreen
  // below, so this has to listen for the real event rather than only
  // updating state inside that one function.
  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // The Fullscreen API only succeeds when called directly from a real
  // user gesture (a click) -- it can't be triggered automatically after
  // the async capture/delay sequence, since by then the browser no
  // longer considers it part of a genuine user interaction. This is
  // why fullscreen here is an explicit button someone taps themselves,
  // rather than something that happens on its own.
  const toggleFullscreen = async () => {
    if (!fullscreenTargetRef.current) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await fullscreenTargetRef.current.requestFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen request failed:", err);
      toast.error("This browser doesn't support fullscreen here, or it needs to be enabled in settings.");
    }
  };

  useEffect(() => () => {
    if (scrollTimerRef.current) window.clearInterval(scrollTimerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    videoRef.current?.pause();
  }, []);

  // --- Capture ---
  const startCapture = async (mode: "single" | "scroll" = "single") => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Screen capture isn't supported in this browser");
      return;
    }
    let stream: MediaStream;
    try {
      // This is the browser's own native "choose what to share" prompt --
      // no website can skip, remove, or replace it with custom UI. It's
      // a hard security boundary: without it, any site could silently
      // capture whatever it wanted from your screen.
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    } catch {
      toast.error("Screenshot wasn't taken (permission denied or cancelled)");
      return;
    }
    streamRef.current = stream;

    // Real, confirmed bug this fixes: a getDisplayMedia stream with no
    // <video> element actually consuming/rendering it can be throttled
    // by the browser to save CPU while nothing's watching it -- so
    // creating the <video> element only AFTER the delay (as this used
    // to do) meant the delay itself achieved nothing: the stream sat
    // unconsumed for the full countdown, and the frame finally grabbed
    // afterward could still be the stale one from right when the
    // window/screen was first selected, not whatever was actually on
    // screen once the countdown ended -- exactly matching a report of
    // an expanded menu, opened only during the countdown, never
    // appearing in the final capture. Attaching and playing the video
    // element immediately, and keeping it playing continuously through
    // the whole countdown, keeps the stream genuinely live the entire
    // time, so the frame grabbed at the end is the frame actually on
    // screen at that moment, not an old one.
    const video = document.createElement("video");
    video.srcObject = stream;
    videoRef.current = video;
    try {
      await video.play();
      await new Promise(resolve => {
        if (video.videoWidth > 0) resolve(null);
        else video.onloadedmetadata = () => resolve(null);
      });
    } catch {
      toast.error("Couldn't start the screen preview");
      stream.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      return;
    }

    if (mode === "scroll") {
      beginScrollCapture(stream, video);
      return;
    }

    if (delaySeconds > 0) {
      setStage("counting-down");
      for (let remaining = delaySeconds; remaining > 0; remaining--) {
        setCountdown(remaining);
        await new Promise(r => setTimeout(r, 1000));
        // The stream can be stopped mid-countdown if the shared tab/window
        // was closed, or the browser's own "stop sharing" control was
        // used -- bail out cleanly rather than trying to grab a frame
        // from a dead stream.
        if (!streamRef.current?.active) {
          toast.error("Screen sharing was stopped before capture");
          videoRef.current = null;
          setStage("idle");
          return;
        }
      }
    }

    await grabFrame(stream, video);
  };

  // --- Whole-page capture ---
  // A browser lets a site see only what is on screen, so it cannot scroll or read another page. The person scrolls the
  // shared tab or window; a frame is taken every 600 ms, lined up with the previous one and added (see stitchScroll.ts).
  // Everything stays in this tab: no frame, and no part of the page, is sent anywhere.
  const stopScrollTimer = () => { if (scrollTimerRef.current) { window.clearInterval(scrollTimerRef.current); scrollTimerRef.current = null; } };

  const beginScrollCapture = (stream: MediaStream, video: HTMLVideoElement) => {
    const stitcher = new ScrollStitcher();
    stitcherRef.current = stitcher;
    const cv = document.createElement("canvas");
    const ctx = cv.getContext("2d", { willReadFrequently: true })!;
    setScrollInfo({ frames: 0, height: 0, hint: SCROLL_HINTS.first });
    setStage("scrolling");
    const tick = () => {
      if (!streamRef.current?.active || !video.videoWidth) return;
      cv.width = video.videoWidth;
      cv.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);
      const img = ctx.getImageData(0, 0, cv.width, cv.height);
      const r = stitcher.add({ data: img.data, width: cv.width, height: cv.height });
      setScrollInfo(prev => ({
        frames: stitcher.frameCount,
        height: stitcher.height,
        hint: r.status === "added" || r.status === "same" ? (prev.frames <= 1 && r.status === "same" ? prev.hint : "") : SCROLL_HINTS[r.status],
      }));
      if (r.status === "limit") finishScrollCapture();
    };
    scrollTimerRef.current = window.setInterval(tick, 600);
    tick();
    // "Stop sharing" in the browser's own bar ends the stream: keep what was captured.
    stream.getVideoTracks()[0]?.addEventListener("ended", () => finishScrollCapture());
  };

  const cancelScrollCapture = () => {
    stopScrollTimer();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    videoRef.current = null;
    stitcherRef.current = null;
    setStage("idle");
  };

  const finishScrollCapture = () => {
    stopScrollTimer();
    const stitcher = stitcherRef.current;
    stitcherRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    videoRef.current = null;
    const out = stitcher?.result();
    if (!stitcher || !out) { setStage("idle"); return; }
    const canvas = document.createElement("canvas");
    canvas.width = out.width;
    canvas.height = out.height;
    canvas.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(out.data), out.width, out.height), 0, 0);
    const frames = stitcher.frameCount;
    canvas.toBlob(blob => {
      if (!blob) {
        toast.error("Couldn't build the whole-page image");
        setStage("idle");
        return;
      }
      setRawCaptureUrl(URL.createObjectURL(blob));
      setSelection(null);
      setStitchNote(frames > 1
        ? `Stitched from ${frames} frames into ${out.width.toLocaleString("en-US")} × ${out.height.toLocaleString("en-US")} px. Only what you scrolled past is in it. Fixed headers and footers appear once; videos and animations are a single frame; check the joins before sharing.`
        : "Only one frame was captured (the page was not scrolled), so this is a normal screenshot of what was on screen.");
      setStage("cropping");
      toast.success(frames > 1 ? "Whole-page capture ready" : "Screenshot captured");
    }, "image/png");
  };

  const grabFrame = async (stream: MediaStream, video: HTMLVideoElement) => {
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);

    stream.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    videoRef.current = null;

    canvas.toBlob(blob => {
      if (!blob) {
        toast.error("Couldn't capture the screenshot");
        setStage("idle");
        return;
      }
      setRawCaptureUrl(URL.createObjectURL(blob));
      setStitchNote(null);
      setSelection(null);
      setStage("cropping");
      toast.success("Screenshot captured");
    }, "image/png");
  };

  // --- Crop (drag-select on the raw captured image) ---
  // The <img> uses object-contain, which means its own CSS box
  // (getBoundingClientRect()) and the actual visible image pixels
  // within that box are only the same when the box's aspect ratio
  // happens to exactly match the image's -- otherwise object-contain
  // letterboxes it (empty space on the sides, or top/bottom), and the
  // element's own bounding box still reports the FULL box, not the
  // smaller, centered area where the image is actually visible. This
  // was the real, confirmed bug: drag coordinates and the final crop's
  // scale math were both measured against the full box, silently
  // ignoring the letterbox gap -- correct only when the box's aspect
  // ratio happened to match, and shifted/partial whenever it didn't
  // (which is the common case for a wide desktop screenshot in a
  // narrower preview box). This computes the real, visible image
  // content rect within that box, using the same math the browser
  // itself uses for object-fit: contain.
  const getContainedImageRect = (img: HTMLImageElement) => {
    const rect = img.getBoundingClientRect();
    const boxRatio = rect.width / rect.height;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    let width: number, height: number, x: number, y: number;
    if (imgRatio > boxRatio) {
      // Image is relatively wider than the box -> letterboxed top/bottom
      width = rect.width;
      height = rect.width / imgRatio;
      x = 0;
      y = (rect.height - height) / 2;
    } else {
      // Image is relatively taller than the box -> letterboxed left/right
      height = rect.height;
      width = rect.height * imgRatio;
      y = 0;
      x = (rect.width - width) / 2;
    }
    return { x, y, width, height, boxWidth: rect.width, boxHeight: rect.height };
  };
  const getRelativePosAt = (clientX: number, clientY: number) => {
    const img = rawImgRef.current;
    if (!img) return { x: 0, y: 0 };
    const rect = img.getBoundingClientRect();
    const content = getContainedImageRect(img);
    // Position within the full element box, then shift by the
    // letterbox offset and clamp to the real visible image content --
    // not the full box, which could extend into empty letterbox space.
    const rawX = clientX - rect.left - content.x;
    const rawY = clientY - rect.top - content.y;
    return {
      x: Math.max(0, Math.min(content.width, rawX)),
      y: Math.max(0, Math.min(content.height, rawY)),
    };
  };
  // Thin wrapper kept so the existing mouse-event call sites below don't
  // need to change -- getRelativePosAt is the one real implementation
  // both mouse and touch paths now share.
  const getRelativePos = (e: React.MouseEvent) => getRelativePosAt(e.clientX, e.clientY);
  // The selection overlay <div> is a sibling of the <img>, both
  // absolutely/relatively positioned within the same container -- its
  // left/top need to be relative to the CONTAINER, so both the
  // image's own offset within that container AND the letterbox offset
  // within the image element itself have to be added back in.
  const getImageOffsetInContainer = () => {
    const containerRect = cropContainerRef.current?.getBoundingClientRect();
    const img = rawImgRef.current;
    if (!containerRect || !img) return { x: 0, y: 0 };
    const imgRect = img.getBoundingClientRect();
    const content = getContainedImageRect(img);
    return {
      x: imgRect.left - containerRect.left + content.x,
      y: imgRect.top - containerRect.top + content.y,
    };
  };
  const onCropMouseDown = (e: React.MouseEvent) => {
    const pos = getRelativePos(e);
    setDragStart(pos);
    setSelection({ x: pos.x, y: pos.y, w: 0, h: 0 });
  };
  const onCropMouseMove = (e: React.MouseEvent) => {
    if (!dragStart) return;
    const pos = getRelativePos(e);
    setSelection({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  };
  // Real touch support -- the same confirmed bug found and fixed
  // across Resize & Crop, Document Scanner, Screen Snip, and Camera
  // Capture: this drag-to-select area only ever had mouse events,
  // which touch interactions on a phone or tablet don't reliably fire
  // for an actual drag/swipe gesture (only touchstart/touchmove/
  // touchend do), so selecting a crop region here simply never worked
  // on a touch device. e.preventDefault() on touchmove stops the page
  // scrolling out from under the drag.
  const onCropTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    const t = e.touches[0];
    const pos = getRelativePosAt(t.clientX, t.clientY);
    setDragStart(pos);
    setSelection({ x: pos.x, y: pos.y, w: 0, h: 0 });
  };
  const onCropTouchMove = (e: React.TouchEvent) => {
    if (!dragStart || e.touches.length === 0) return;
    e.preventDefault();
    const t = e.touches[0];
    const pos = getRelativePosAt(t.clientX, t.clientY);
    setSelection({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  };
  const onCropMouseUp = () => setDragStart(null);

  // Shared by both "Crop" (which hands off into the full editor) and
  // the quick Copy/Download buttons below (for someone who just wants
  // the plain capture, or their drag-selected region, without opening
  // the editor at all) -- one real implementation of the crop math
  // instead of two copies that could drift out of sync with each
  // other.
  const buildCroppedCanvas = (): HTMLCanvasElement | null => {
    const img = rawImgRef.current;
    if (!img) return null;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    if (selection && selection.w >= 4 && selection.h >= 4) {
      const content = getContainedImageRect(img);
      const scaleX = img.naturalWidth / content.width;
      const scaleY = img.naturalHeight / content.height;
      // Round the EDGES (not the width/height) so the file is exactly the size of the box, never 1 px short.
      const x0 = Math.max(0, Math.round(selection.x * scaleX));
      const y0 = Math.max(0, Math.round(selection.y * scaleY));
      const x1 = Math.min(img.naturalWidth, Math.round((selection.x + selection.w) * scaleX));
      const y1 = Math.min(img.naturalHeight, Math.round((selection.y + selection.h) * scaleY));
      canvas.width = Math.max(1, x1 - x0);
      canvas.height = Math.max(1, y1 - y0);
      ctx.drawImage(img, x0, y0, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
    } else {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
    }
    return canvas;
  };

  // Moves from the crop stage into the editor, either with the
  // dragged selection or the full raw capture if nothing was selected.
  const proceedToEditor = () => {
    const canvas = buildCroppedCanvas();
    if (!canvas) return;
    loadIntoEditor(canvas.toDataURL("image/png"));
  };

  // Lets someone who doesn't want to crop or mark anything up just
  // copy or download the plain capture (or their current drag
  // selection, if they made one) directly from this stage, instead of
  // being forced through the full editor to reach the same two
  // buttons.
  const quickCopy = async () => {
    const canvas = buildCroppedCanvas();
    if (!canvas) return;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) return;
    await copyImage(blob);
  };
  // Copying the picture takes it off the page like a download does, so it goes through the same share gate.
  const copyImage = (blob: Blob) => gateShare(async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("Copied — paste it anywhere");
      return true;
    } catch (err) {
      console.error("Clipboard write failed:", err);
      toast.error("Couldn't copy to clipboard. Try Download instead.");
      return false;
    }
  });
  const quickDownload = () => {
    const canvas = buildCroppedCanvas();
    if (!canvas) return;
    canvas.toBlob((blob) => { if (blob) downloadBlob(blob, shotName()); }, shotFormat === "jpg" ? "image/jpeg" : "image/png", 0.92);
  };

  // --- Editor (rotate, zoom, pencil, highlighter) ---
  const loadIntoEditor = (dataUrl: string) => {
    const img = new Image();
    img.onload = () => {
      // The <canvas> this needs to draw onto only exists in the DOM
      // once stage === "editing" actually renders -- calling
      // redrawEditor directly here, before setStage takes effect,
      // would find editCanvasRef.current still null and silently do
      // nothing, then a freshly-mounted, empty canvas would appear
      // with nothing ever drawn onto it. Storing the image and letting
      // the effect below do the actual drawing (which only runs after
      // React has committed the stage change and mounted the canvas)
      // is what makes this reliable.
      cropImgRef.current = img;
      pendingEditorLoadRef.current = { img, rot: 0 };
      setRotation(0);
      setZoom(1);
      setUndoStack([]);
      setActiveTool("select");
      setStage("editing");
    };
    img.src = dataUrl;
  };

  useEffect(() => {
    if (stage !== "editing" || !pendingEditorLoadRef.current) return;
    const { img, rot } = pendingEditorLoadRef.current;
    pendingEditorLoadRef.current = null;
    redrawEditor(img, rot);
  }, [stage]);

  const redrawEditor = (img: HTMLImageElement, rot: Rotation) => {
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    const swap = rot === 90 || rot === 270;
    canvas.width = swap ? img.naturalHeight : img.naturalWidth;
    canvas.height = swap ? img.naturalWidth : img.naturalHeight;
    const ctx = canvas.getContext("2d")!;
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    ctx.restore();
  };

  const rotate = () => {
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    // Rotating the current canvas content -- not the original,
    // pre-annotation image -- matters here: redrawing from the
    // pristine source on every rotate would silently discard any
    // pencil/highlighter strokes already drawn, which is exactly the
    // kind of thing someone wouldn't notice until it was too late.
    const currentSnapshot = new Image();
    currentSnapshot.onload = () => {
      const next = ((rotation + 90) % 360) as Rotation;
      setRotation(next);
      setUndoStack([]);
      redrawEditor(currentSnapshot, 90);
    };
    currentSnapshot.src = canvas.toDataURL("image/png");
  };

  const pushUndoSnapshot = () => {
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    setUndoStack(prev => [...prev, canvas.toDataURL("image/png")]);
  };

  const undo = () => {
    const canvas = editCanvasRef.current;
    if (!canvas || undoStack.length === 0) return;
    const prevUrl = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext("2d")!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = prevUrl;
  };

  const getCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = editCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  /** Line width for arrows and boxes: follows the thickness slider, scaled up for large screenshots. */
  const shapeWidth = () => Math.max(2, penThickness * Math.max(1, (editCanvasRef.current?.width ?? 1000) / 1000));

  const drawShape = (tool: DrawTool, from: { x: number; y: number }, to: { x: number; y: number }) => {
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const w = shapeWidth();
    ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = penColor; ctx.fillStyle = penColor; ctx.lineWidth = w;
    if (tool === "rect") {
      ctx.strokeRect(Math.min(from.x, to.x), Math.min(from.y, to.y), Math.abs(to.x - from.x), Math.abs(to.y - from.y));
    } else if (tool === "arrow") {
      const dx = to.x - from.x, dy = to.y - from.y, len = Math.hypot(dx, dy);
      if (len < 2) return;
      const head = Math.min(len * 0.6, w * 5 + 6), a = Math.atan2(dy, dx);
      // The line stops at the base of the head so a thick line does not poke out of the tip.
      const bx = to.x - Math.cos(a) * head * 0.8, by = to.y - Math.sin(a) * head * 0.8;
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(bx, by); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(to.x - head * Math.cos(a - 0.45), to.y - head * Math.sin(a - 0.45));
      ctx.lineTo(to.x - head * Math.cos(a + 0.45), to.y - head * Math.sin(a + 0.45));
      ctx.closePath(); ctx.fill();
    } else if (tool === "blur") {
      // Pixelates the box (hides text such as names or numbers); a plain blur can be partly reversed, big blocks cannot.
      const x = Math.max(0, Math.round(Math.min(from.x, to.x))), y = Math.max(0, Math.round(Math.min(from.y, to.y)));
      const bw = Math.min(canvas.width - x, Math.round(Math.abs(to.x - from.x))), bh = Math.min(canvas.height - y, Math.round(Math.abs(to.y - from.y)));
      if (bw < 2 || bh < 2) return;
      const block = Math.max(6, Math.round(Math.max(bw, bh) / 12));
      const tmp = document.createElement("canvas");
      tmp.width = Math.max(1, Math.ceil(bw / block)); tmp.height = Math.max(1, Math.ceil(bh / block));
      const t = tmp.getContext("2d")!;
      t.imageSmoothingEnabled = true;
      t.drawImage(canvas, x, y, bw, bh, 0, 0, tmp.width, tmp.height);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tmp, 0, 0, tmp.width, tmp.height, x, y, bw, bh);
      ctx.imageSmoothingEnabled = true;
    }
  };

  const commitText = () => {
    const d = textDraft;
    setTextDraft(null);
    const canvas = editCanvasRef.current;
    if (!d || !canvas || !d.value.trim()) return;
    pushUndoSnapshot();
    const ctx = canvas.getContext("2d")!;
    const size = Math.round(Math.max(16, penThickness * 5) * Math.max(1, canvas.width / 1000));
    ctx.font = `600 ${size}px system-ui, "Segoe UI", Roboto, "Noto Sans", "Noto Sans Devanagari", sans-serif`;
    ctx.textBaseline = "top"; ctx.fillStyle = penColor;
    // A thin opposite-colour edge keeps the words readable on any background.
    ctx.lineWidth = Math.max(2, size / 9); ctx.lineJoin = "round";
    ctx.strokeStyle = penColor === "#ffffff" ? "#000000" : "#ffffff";
    d.value.split("\n").forEach((line, i) => { ctx.strokeText(line, d.cx, d.cy + i * size * 1.2); ctx.fillText(line, d.cx, d.cy + i * size * 1.2); });
  };

  const onCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activeTool === "select") return;
    if (activeTool === "text") {
      e.preventDefault();
      if (textDraft) commitText();
      const pt = getCanvasPoint(e);
      setTextDraft({ cx: pt.x, cy: pt.y, left: e.clientX, top: e.clientY, value: "" });
      return;
    }
    if (SHAPE_TOOLS.includes(activeTool)) {
      pushUndoSnapshot();
      const canvas = editCanvasRef.current!;
      const pt = getCanvasPoint(e);
      shapeRef.current = { start: pt, base: canvas.getContext("2d")!.getImageData(0, 0, canvas.width, canvas.height) };
      isDrawingRef.current = true;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      return;
    }
    pushUndoSnapshot();
    isDrawingRef.current = true;
    const pt = getCanvasPoint(e);
    lastPointRef.current = pt;
    drawDot(pt);
  };
  const onCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activeTool === "select" || !isDrawingRef.current) return;
    const pt = getCanvasPoint(e);
    if (shapeRef.current) {
      const canvas = editCanvasRef.current!;
      canvas.getContext("2d")!.putImageData(shapeRef.current.base, 0, 0);
      drawShape(activeTool, shapeRef.current.start, pt);
      return;
    }
    drawSegment(lastPointRef.current, pt);
    lastPointRef.current = pt;
  };
  const onCanvasPointerUp = () => {
    shapeRef.current = null;
    isDrawingRef.current = false;
    lastPointRef.current = null;
  };

  const drawDot = (pt: { x: number; y: number }) => drawSegment(pt, pt);
  const drawSegment = (from: { x: number; y: number } | null, to: { x: number; y: number }) => {
    if (!from) return;
    const canvas = editCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (activeTool === "pencil") {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penThickness;
    } else if (activeTool === "highlighter") {
      // Semi-transparent so marked content stays legible underneath --
      // this is a single flat canvas, so a highlighter stroke still
      // paints over anything already drawn at that point (pencil marks
      // included), it's just partially see-through rather than opaque.
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = highlighterColor;
      ctx.lineWidth = highlighterThickness;
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.globalAlpha = 1;
  };

  // --- Output ---
  const getFinalBlob = (): Promise<Blob | null> => {
    const canvas = editCanvasRef.current;
    if (!canvas) return Promise.resolve(null);
    return new Promise(resolve => canvas.toBlob(b => resolve(b), shotFormat === "jpg" ? "image/jpeg" : "image/png", 0.92));
  };
  const shotName = () => { const d = new Date(); const z = (n: number) => String(n).padStart(2, "0"); return `screenshot-${d.getFullYear()}${z(d.getMonth() + 1)}${z(d.getDate())}-${z(d.getHours())}${z(d.getMinutes())}${z(d.getSeconds())}.${shotFormat}`; };

  const download = async () => {
    const blob = await getFinalBlob();
    if (!blob) return;
    downloadBlob(blob, shotName());
  };

  const copyToClipboard = async () => {
    const blob = await getFinalBlob();
    if (!blob) return;
    await copyImage(blob);
  };

  const reset = () => {
    setStage("idle");
    setRawCaptureUrl(null);
    setStitchNote(null);
    setSelection(null);
    setUndoStack([]);
  };

  return (
    <ToolContainer>
      {stage === "idle" && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-xl space-y-5">
          <Camera className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground max-w-md mx-auto px-4">
            Capture a screenshot of your screen, a window, or a browser tab — entirely in your browser.
            Nothing is uploaded anywhere.
          </p>
          <div>
            <p className="text-xs text-muted-foreground mb-2">Delay before capturing — gives you time to switch to another window after picking "Entire Screen," since your browser itself drops to the background right after you choose that option</p>
            <div className="flex items-center justify-center gap-2">
              {DELAY_OPTIONS.map(d => (
                <Button key={d} size="sm" variant={delaySeconds === d ? "default" : "outline"} onClick={() => setDelaySeconds(d)}>
                  {d === 0 ? "None" : `${d}s`}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button onClick={() => startCapture("single")} size="lg">
              <Camera className="h-4 w-4 mr-2" /> Take Screenshot
            </Button>
            <Button onClick={() => startCapture("scroll")} size="lg" variant="outline">
              <ScrollText className="h-4 w-4 mr-2" /> Capture whole page (scroll)
            </Button>
          </div>
          <details className="mx-auto max-w-xl px-4 text-left text-xs text-muted-foreground">
            <summary className="cursor-pointer text-center">How whole-page capture works, and what it can't capture</summary>
            <div className="mt-2 space-y-2">
              <p>A website can't read or scroll another page for you, so there is no one-click full-page capture here. Instead, share the tab or window, then scroll it yourself, slowly. This tool takes a picture about twice a second, lines each one up with the last, and joins them into one tall image, all in your browser. Nothing is uploaded.</p>
              <p>Not captured: anything you don't scroll past; content that only loads after a pause if you scroll on too quickly; side-to-side scrolling; videos and animations (they show as one frame); anything covering the page, such as other windows or pop-ups. Sticky headers and footers appear once. Long blank stretches or repeating patterns can't be lined up, and the tool says so instead of guessing. The image is capped at {MAX_STITCH_HEIGHT.toLocaleString("en-US")} px tall.</p>
            </div>
          </details>
        </div>
      )}

      {stage === "scrolling" && (
        <div className="text-center py-10 border-2 border-dashed border-border rounded-xl space-y-4 px-4" data-testid="scroll-capture">
          <ScrollText className="h-9 w-9 mx-auto text-primary" />
          <p className="text-sm font-medium">Recording the page you shared</p>
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            {scrollInfo.frames <= 1 ? "Switch to the shared tab or window and scroll down slowly." : `Captured ${scrollInfo.height.toLocaleString("en-US")} px so far from ${scrollInfo.frames} frames.`}
          </p>
          {scrollInfo.hint && <p className="text-sm max-w-md mx-auto" role="status">{scrollInfo.hint}</p>}
          <div className="flex justify-center gap-3">
            <Button onClick={finishScrollCapture}>Finish</Button>
            <Button variant="outline" onClick={cancelScrollCapture}>Cancel</Button>
          </div>
          <p className="text-xs text-muted-foreground">Scroll steady and not too fast: each step must overlap the last. Press Finish here, or use the browser's Stop sharing, when you reach the end.</p>
        </div>
      )}

      {stage === "counting-down" && (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-xl">
          <p className="text-sm text-muted-foreground mb-3">Set up your screen now — capturing in</p>
          <p className="text-6xl font-bold text-primary">{countdown}</p>
        </div>
      )}

      {stage === "cropping" && rawCaptureUrl && (
        <div ref={fullscreenTargetRef} className={isFullscreen ? "bg-background p-4 h-screen flex flex-col justify-center space-y-4" : "space-y-4"}>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={toggleFullscreen} className="gap-2">
              {isFullscreen ? <><Minimize className="h-4 w-4" /> Exit Fullscreen</> : <><Maximize className="h-4 w-4" /> Fullscreen (easier on mobile)</>}
            </Button>
          </div>
          <div
            ref={cropContainerRef}
            className={`relative w-full rounded-lg border overflow-hidden bg-muted/30 cursor-crosshair select-none ${isFullscreen ? "flex-1" : "max-h-[70vh]"}`}
            onMouseDown={onCropMouseDown}
            onMouseMove={onCropMouseMove}
            onMouseUp={onCropMouseUp}
            onMouseLeave={onCropMouseUp}
            onTouchStart={onCropTouchStart}
            onTouchMove={onCropTouchMove}
            onTouchEnd={onCropMouseUp}
            onTouchCancel={onCropMouseUp}
            style={{ touchAction: "none" }}
          >
            <img
              ref={rawImgRef}
              src={rawCaptureUrl}
              alt="Captured screenshot"
              className={isFullscreen ? "w-full h-full object-contain pointer-events-none" : "w-full max-h-[70vh] object-contain pointer-events-none"}
              draggable={false}
            />
            {selection && selection.w >= 4 && selection.h >= 4 && (() => {
              const offset = getImageOffsetInContainer();
              return (
                <div
                  className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                  style={{ left: offset.x + selection.x, top: offset.y + selection.y, width: selection.w, height: selection.h }}
                />
              );
            })()}
          </div>
          {stitchNote && <p className="text-xs text-muted-foreground text-center" data-testid="stitch-note">{stitchNote}</p>}
          <p className="text-xs text-muted-foreground text-center">
            Drag across the image to select just the region you want, then Crop to open the editor for rotating or
            marking it up — or Copy/Download the plain capture (or your selection) directly if that's all you need.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button onClick={proceedToEditor} className="gap-2">
              <Crop className="h-4 w-4" /> Crop
            </Button>
            {copySupported && (
              <Button variant="outline" onClick={quickCopy} className="gap-2">
                <Copy className="h-4 w-4" /> Copy
              </Button>
            )}
            <Button variant="outline" onClick={quickDownload} className="gap-2">
              Download
            </Button>
            <Button variant="outline" onClick={reset}>Take Another</Button>
          </div>
        </div>
      )}

      {stage === "editing" && (
        <div ref={fullscreenTargetRef} className={isFullscreen ? "bg-background p-4 h-screen overflow-auto space-y-4" : "space-y-4"}>
          <div className="flex items-center justify-center gap-2 flex-wrap border-b pb-3">
            <Button size="sm" variant={activeTool === "select" ? "default" : "outline"} onClick={() => setActiveTool("select")} title="Select / pan"><MousePointer2 className="h-4 w-4" /></Button>
            <Button size="sm" variant={activeTool === "pencil" ? "default" : "outline"} onClick={() => setActiveTool("pencil")} title="Pencil"><Pencil className="h-4 w-4" /></Button>
            <Button size="sm" variant={activeTool === "highlighter" ? "default" : "outline"} onClick={() => setActiveTool("highlighter")} title="Highlighter"><Highlighter className="h-4 w-4" /></Button>
            <Button size="sm" variant={activeTool === "arrow" ? "default" : "outline"} onClick={() => setActiveTool("arrow")} title="Arrow"><MoveUpRight className="h-4 w-4" /></Button>
            <Button size="sm" variant={activeTool === "rect" ? "default" : "outline"} onClick={() => setActiveTool("rect")} title="Box"><Square className="h-4 w-4" /></Button>
            <Button size="sm" variant={activeTool === "text" ? "default" : "outline"} onClick={() => setActiveTool("text")} title="Text"><Type className="h-4 w-4" /></Button>
            <Button size="sm" variant={activeTool === "blur" ? "default" : "outline"} onClick={() => setActiveTool("blur")} title="Hide (pixelate) a region"><Grid2x2 className="h-4 w-4" /></Button>
            <div className="w-px h-6 bg-border mx-1" />
            <Button size="sm" variant="outline" onClick={rotate} title="Rotate 90°"><RotateCw className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} title="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
            <span className="text-xs text-muted-foreground w-10 text-center">{Math.round(zoom * 100)}%</span>
            <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.min(3, z + 0.25))} title="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={undo} disabled={undoStack.length === 0} title="Undo last stroke"><Undo2 className="h-4 w-4" /></Button>
            <div className="w-px h-6 bg-border mx-1" />
            <Button size="sm" variant="outline" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen (easier on mobile)"}>
              {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </Button>
          </div>

          {(activeTool === "pencil" || activeTool === "arrow" || activeTool === "rect" || activeTool === "text") && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                {PENCIL_COLORS.map(c => (
                  <button
                    key={c} onClick={() => setPenColor(c)}
                    className={`h-6 w-6 rounded-full border-2 ${penColor === c ? "border-primary" : "border-border"}`}
                    style={{ backgroundColor: c }} aria-label={`Pencil color ${c}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 w-40">
                <span className="text-xs text-muted-foreground shrink-0">Thickness</span>
                <Slider value={[penThickness]} onValueChange={(v) => setPenThickness(v[0])} min={1} max={12} step={1} />
              </div>
            </div>
          )}
          {activeTool === "highlighter" && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                {HIGHLIGHTER_COLORS.map(c => (
                  <button
                    key={c} onClick={() => setHighlighterColor(c)}
                    className={`h-6 w-6 rounded-full border-2 ${highlighterColor === c ? "border-primary" : "border-border"}`}
                    style={{ backgroundColor: c }} aria-label={`Highlighter color ${c}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 w-40">
                <span className="text-xs text-muted-foreground shrink-0">Thickness</span>
                <Slider value={[highlighterThickness]} onValueChange={(v) => setHighlighterThickness(v[0])} min={8} max={40} step={2} />
              </div>
            </div>
          )}

          {activeTool === "blur" && <p className="text-center text-xs text-muted-foreground">Drag a box over anything you want to hide, such as a name or a number. It is pixelated into large blocks.</p>}
          {activeTool === "text" && <p className="text-center text-xs text-muted-foreground">Click where the text should go, type, then press Enter (Shift+Enter for a new line).</p>}
          {textDraft && (
            <textarea
              autoFocus rows={1} value={textDraft.value}
              onChange={(e) => setTextDraft((d) => (d ? { ...d, value: e.target.value } : d))}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitText(); } else if (e.key === "Escape") setTextDraft(null); }}
              onBlur={() => { if (textDraft.value.trim()) commitText(); }}
              aria-label="Text to add"
              className="fixed z-50 border rounded px-1 text-base bg-white text-black shadow resize-none"
              style={{ left: textDraft.left, top: textDraft.top, minWidth: 160 }}
            />
          )}
          <div className="w-full rounded-lg border max-h-[70vh] overflow-auto bg-muted/30 flex items-center justify-center">
            <canvas
              ref={editCanvasRef}
              onPointerDown={onCanvasPointerDown}
              onPointerMove={onCanvasPointerMove}
              onPointerUp={onCanvasPointerUp}
              onPointerLeave={onCanvasPointerUp}
              onMouseDown={(e) => { if (activeTool === "text") e.preventDefault(); /* keep focus for the text box that opens */ }}
              className="touch-none"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "center",
                cursor: activeTool === "select" ? "default" : activeTool === "text" ? "text" : "crosshair",
                maxWidth: zoom === 1 ? "100%" : undefined,
              }}
            />
          </div>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            {copySupported ? (
              <Button onClick={copyToClipboard} className="gap-2"><Copy className="h-4 w-4" /> Copy to Clipboard</Button>
            ) : (
              <Button disabled title="Your browser doesn't support copying images to the clipboard" className="gap-2">
                <Copy className="h-4 w-4" /> Copy Not Supported
              </Button>
            )}
            <select className="border rounded px-2 text-sm bg-background" value={shotFormat} onChange={e => setShotFormat(e.target.value as "png" | "jpg")} aria-label="File format"><option value="png">PNG</option><option value="jpg">JPG (smaller)</option></select>
            <DownloadButton onClick={download} markHint={shotName()} label={`Download ${shotFormat.toUpperCase()}`} />
            <Button variant="outline" onClick={reset}>Take Another</Button>
          </div>
        </div>
      )}

      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
